import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { defaultOrganizationName, useAuth } from '../auth/AuthProvider';
import { CheckIcon } from './icons';
import { Button, ErrorText, Input, cx } from './ui';
import { errorMessage } from '../lib/api';
import type { BillingInterval, Pricing } from '../lib/types';
import { createOrg, seatPriceLabel, trialLabel, yearlySavingsPercent } from '../queries/orgs';

/**
 * The two plans, side by side. Used on the welcome page right after signup
 * and again on the subscription tab, so the offer reads the same in both.
 */

/** Monthly or yearly, with what the year saves. Hidden when only monthly exists. */
export function IntervalToggle({
  pricing,
  value,
  onChange,
}: {
  pricing: Pricing | null;
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
}) {
  if (!pricing || pricing.seatYearlyCents === null) return null;
  const savings = yearlySavingsPercent(pricing);
  return (
    <div className="inline-flex rounded border border-hairline bg-slate-850 p-0.5" role="radiogroup" aria-label="Billing interval">
      {(['month', 'year'] as const).map((interval) => (
        <button
          key={interval}
          type="button"
          role="radio"
          aria-checked={value === interval}
          onClick={() => onChange(interval)}
          className={cx(
            'rounded-xs px-3 py-1.5 text-body transition-colors duration-120',
            value === interval ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk',
          )}
        >
          {interval === 'month' ? 'Monthly' : 'Yearly'}
          {interval === 'year' && savings > 0 ? <span className="ml-1.5 text-caption text-go">save {savings}%</span> : null}
        </button>
      ))}
    </div>
  );
}

function Point({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-body text-chalk-dim">
      <CheckIcon size={15} className="mt-1 shrink-0 text-go" />
      <span>{children}</span>
    </li>
  );
}

export function PlanCard({
  name,
  price,
  cadence,
  eyebrow,
  points,
  highlighted = false,
  children,
}: {
  name: string;
  price: string;
  cadence: string;
  eyebrow?: string;
  points: string[];
  highlighted?: boolean;
  children?: ReactNode;
}) {
  return (
    <section
      className={cx(
        'flex flex-col rounded-md border p-5 sm:p-6',
        highlighted ? 'border-signal/60 bg-slate-900' : 'border-hairline bg-slate-900/60',
      )}
    >
      {eyebrow ? (
        <p className={cx('mb-2 text-micro uppercase tracking-[0.1em]', highlighted ? 'text-signal' : 'text-chalk-faint')}>
          {eyebrow}
        </p>
      ) : null}
      <h2 className="text-h3 text-chalk">{name}</h2>
      <p className="mt-2 flex items-baseline gap-1.5">
        <span className="text-h1 text-chalk tnum">{price}</span>
        <span className="text-caption text-chalk-faint">{cadence}</span>
      </p>
      <ul className="mt-5 space-y-2.5">
        {points.map((point) => (
          <Point key={point}>{point}</Point>
        ))}
      </ul>
      <div className="mt-6 flex-1" />
      {children}
    </section>
  );
}

/**
 * Starting a team: a name, then straight to checkout when billing is
 * configured, or straight into the trial when it is not. Lives in the card
 * so the plan and the action are one thing.
 */
export function StartTeam({
  pricing,
  interval = 'month',
  onCreated,
  buttonLabel,
}: {
  pricing: Pricing | null;
  interval?: BillingInterval;
  onCreated: (org: { id: string; slug: string }) => void;
  buttonLabel?: string;
}) {
  const [naming, setNaming] = useState(false);
  const { user } = useAuth();
  const [name, setName] = useState(() => defaultOrganizationName(user));
  const [invalid, setInvalid] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (value: string) => createOrg(value, interval),
    onSuccess: (response) => {
      if (response.checkoutUrl) {
        window.location.assign(response.checkoutUrl);
        return;
      }
      onCreated(response.org);
    },
  });

  const trial = trialLabel(pricing, interval);
  const label = buttonLabel ?? (trial ? 'Start free trial' : 'Start a team');
  const price = seatPriceLabel(pricing, interval);

  if (!naming) {
    return (
      <Button variant="primary" size="lg" className="w-full" onClick={() => setNaming(true)}>
        {label}
      </Button>
    );
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        setInvalid(null);
        const trimmed = name.trim();
        if (trimmed.length < 2) {
          setInvalid('Give the organization a name, usually your company or group.');
          return;
        }
        create.mutate(trimmed);
      }}
    >
      <Input
        label="Organization name"
        placeholder="Northwind Design"
        value={name}
        maxLength={80}
        autoFocus
        onChange={(event) => setName(event.target.value)}
      />
      {invalid ? <ErrorText>{invalid}</ErrorText> : null}
      {create.isError ? <ErrorText>{errorMessage(create.error)}</ErrorText> : null}
      <Button type="submit" variant="primary" size="lg" className="w-full" busy={create.isPending}>
        {pricing?.enabled ? 'Continue to checkout' : 'Create team'}
      </Button>
      <p className="text-center text-caption text-chalk-faint">
        {pricing?.enabled
          ? trial
            ? `${pricing.trialDays} days free, then ${price.price} ${price.cadence}. Cancel any time.`
            : `${price.price} ${price.cadence}. Cancel any time.`
          : 'No card needed on this deployment.'}
      </p>
    </form>
  );
}
